import type { ChatMessage } from '@multimod/shared';
import { Clock } from 'lucide-react';
import {
  DialogShell,
  DialogFooter,
  DialogCancel,
  PLATFORM_NAME,
  arrowNavHandler,
} from './_shell';

const PRESETS: Array<{ label: string; seconds: number }> = [
  { label: '1m', seconds: 60 },
  { label: '5m', seconds: 300 },
  { label: '10m', seconds: 600 },
  { label: '1h', seconds: 3600 },
  { label: '24h', seconds: 86400 },
];

interface Props {
  open: boolean;
  msg: ChatMessage;
  onOpenChange: (open: boolean) => void;
  onConfirm: (seconds: number) => void;
}

export function TimeoutDialog({ open, msg, onOpenChange, onConfirm }: Props) {
  const platformName = PLATFORM_NAME[msg.platform];
  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      icon={<Clock className="w-5 h-5" aria-hidden="true" />}
      iconTone="warning"
      title={`Silenciar @${msg.author.displayName} por:`}
      description={
        <>
          Silenciar na <strong className="text-fg font-medium">{platformName}</strong> pelo tempo
          escolhido. Você pode revogar antes do prazo.
        </>
      }
      msg={msg}
      initialFocusSelector="button[data-preset]"
      // Decisão: sem ReversibilityNote aqui. Description já carrega o aviso
      // ("Você pode revogar antes do prazo") e timeout é temporário por
      // natureza — hierarquia visual mais leve que ban/delete.
      onKeyDown={(e) => {
        // 1–5 selecionam o preset N (aria-keyshortcuts anunciado nos botões).
        if (e.key >= '1' && e.key <= String(PRESETS.length)) {
          const preset = PRESETS[Number(e.key) - 1];
          if (preset) onConfirm(preset.seconds);
        }
      }}
    >
      <div
        className="flex flex-wrap gap-1.5"
        role="group"
        aria-label="Duração do timeout"
        onKeyDown={arrowNavHandler}
      >
        {PRESETS.map((p, i) => (
          <button
            key={p.label}
            type="button"
            data-preset={p.label}
            onClick={() => onConfirm(p.seconds)}
            aria-keyshortcuts={`${i + 1}`}
            className="px-3 h-11 min-w-[44px] rounded-md border border-border text-sm tabular-nums hover:bg-warning/10 hover:text-warning hover:border-warning/30 focus-visible:ring-1 focus-visible:ring-warning focus-visible:outline-none"
          >
            {p.label}
          </button>
        ))}
      </div>
      <DialogFooter
        hint={
          <>
            <kbd className="px-1.5 py-0.5 rounded bg-bg-muted text-fg-muted">1–5</kbd> ou{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-bg-muted text-fg-muted">←→</kbd> ·{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-bg-muted text-fg-muted">Esc</kbd> cancela
          </>
        }
      >
        <DialogCancel />
      </DialogFooter>
    </DialogShell>
  );
}
